<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Icon from "./Icon.vue";
import { api, when, type FeedbackVia, type Pricing, type Progress, type RunFeedbackEntry, type RunSummary, type SupportRequest } from "../api";
import { href } from "../router";
import { modeText, runPill } from "../status";
import LogStream from "./LogStream.vue";
import ScanProgress from "./ScanProgress.vue";
import AssistChat from "./AssistChat.vue";

// Strona pojedynczej naprawy. Dla klienta liczą się trzy rzeczy, w tej
// kolejności: co się dzieje / co wyszło, czy strona działa (ankieta), i jak to
// cofnąć. Lista plików z werdyktami to szczegóły techniczne — zwinięte.

const props = defineProps<{
  runId: string;
  pricing: Pricing | null;
  /** Identyfikator zadania, gdy ta naprawa właśnie trwa — podpina log na żywo. */
  liveJobId: string | null;
}>();
const emit = defineEmits<{ restored: []; changed: []; done: [number] }>();

interface Finding {
  path: string;
  verdict: string;
  confidence: number;
  heuristicScore: number;
  actionTaken: string;
  note?: string;
}
interface SysFinding { category: string; severity: string; title: string; detail: string }
interface Detail {
  run: RunSummary;
  /** false, gdy admin ogląda naprawę innego klienta. */
  own: boolean;
  manifest: { connection: { target: string }; actions: unknown[] } | null;
  report: { findings?: Finding[]; systemFindings?: SysFinding[] } | null;
  reportMd: string | null;
  scanLog: string | null;
  /** Historia odpowiedzi na „czy strona działa?" i odpowiedzi admina. */
  feedback: RunFeedbackEntry[];
}

const data = ref<Detail | null>(null);
const err = ref("");
const restoreJob = ref<string | null>(null);

const feedbackMsg = ref("");
// Dopracowywanie naprawy: klient pisze, CO nie działa (zawsze, niezależnie od
// drogi), potem wybiera asystenta AI (w cenie) albo płatnego devopsa. Po
// poprawce wraca i odpowiada znowu — „działa" albo „nadal nie / teraz co
// innego". `askingHelp` otwiera formularz opisu; `wantDevops` pokazuje krok
// z akceptacją stawki.
const askingHelp = ref(false);
const wantDevops = ref(false);
const showChat = ref(false);
/** Opis problemu do wysłania asystentowi jako pierwsza wiadomość (raz). */
const chatSeed = ref<string | null>(null);
const feedbackBusy = ref(false);
const feedbackInfo = ref("");
const support = ref<SupportRequest | null>(null);
const history = computed(() => data.value?.feedback ?? []);
const lastProblem = computed(() => [...history.value].reverse().find((e) => e.kind === "problem") ?? null);
const lastReply = computed(() => {
  // Odpowiedź admina liczy się tylko, jeśli przyszła PO ostatnim zgłoszeniu.
  const h = history.value;
  const i = lastProblem.value ? h.findIndex((e) => e.id === lastProblem.value!.id) : -1;
  return [...h.slice(i + 1)].reverse().find((e) => e.kind === "reply") ?? null;
});
const kindText: Record<RunFeedbackEntry["kind"], string> = { ok: "działa", problem: "nie działa", reply: "odpowiedź" };
const viaText: Record<FeedbackVia, string> = { none: "", assist: "asystent AI", devops: "devops" };

async function load() {
  err.value = "";
  try {
    data.value = await api.get<Detail>(`/api/runs/${encodeURIComponent(props.runId)}`);
  } catch (e) {
    err.value = (e as Error).message;
  }
}
watch(
  () => props.runId,
  () => {
    data.value = null;
    restoreJob.value = null;
    support.value = null;
    feedbackInfo.value = "";
    askingHelp.value = false;
    wantDevops.value = false;
    showChat.value = false;
    chatSeed.value = null;
    feedbackMsg.value = "";
    void load();
  },
  { immediate: true },
);

// Postęp z SSE wprost do widoku — bez odpytywania API w trakcie skanu.
function onProgress(p: Progress) {
  if (data.value) data.value.run = { ...data.value.run, progress: p };
}
async function onDone(code: number) {
  await load();
  emit("done", code);
}

const run = computed(() => data.value?.run ?? null);
const finished = computed(() => !!run.value && run.value.status !== "running" && !props.liveJobId);
const isOwner = computed(() => data.value?.own !== false);
const showFeedback = computed(() => finished.value && isOwner.value && run.value?.mode === "quarantine" && run.value.status !== "failed");
const canChat = computed(() => finished.value && run.value?.status !== "failed");

function openChat() {
  showChat.value = true;
  wantDevops.value = false;
}
function escalate() {
  askingHelp.value = true;
  wantDevops.value = true;
  if (!feedbackMsg.value && lastProblem.value) feedbackMsg.value = lastProblem.value.message;
}
function reportAgain() {
  feedbackMsg.value = "";
  wantDevops.value = false;
  askingHelp.value = true;
}
const msgOk = computed(() => feedbackMsg.value.trim().length >= 5);
const hourly = computed(() => props.pricing?.devopsHourlyPln ?? 200);
const problems = computed(() =>
  (data.value?.report?.findings ?? []).filter((f) => f.verdict !== "clean"),
);

/**
 * ok=true: „działa" (zamyka sprawę). ok=false: opis problemu + droga poprawki.
 * Dla asystenta opis idzie od razu jako pierwsza wiadomość czatu.
 */
async function sendFeedback(ok: boolean, via: FeedbackVia = "none") {
  const message = feedbackMsg.value.trim();
  if (!ok && !msgOk.value) {
    feedbackInfo.value = "Napisz w kilku słowach, co nie działa.";
    return;
  }
  feedbackBusy.value = true;
  feedbackInfo.value = "";
  try {
    const d = await api.post<{ run: RunSummary; supportRequest: SupportRequest | null; feedback: RunFeedbackEntry[] }>(
      `/api/runs/${encodeURIComponent(props.runId)}/feedback`,
      { ok, message: ok ? undefined : message, via },
    );
    if (data.value) {
      data.value.run = { ...data.value.run, feedback: d.run.feedback, feedbackAt: d.run.feedbackAt };
      data.value.feedback = d.feedback;
    }
    support.value = d.supportRequest;
    askingHelp.value = false;
    wantDevops.value = false;
    if (ok) feedbackInfo.value = "Dziękujemy za potwierdzenie.";
    else if (via === "devops") feedbackInfo.value = "Zgłoszenie przyjęte — devops odezwie się na Twój adres e-mail. Po poprawce wróć tu i potwierdź, czy działa.";
    else {
      feedbackInfo.value = "";
      chatSeed.value = message;
      showChat.value = true;
    }
    feedbackMsg.value = "";
    emit("changed");
  } catch (e) {
    feedbackInfo.value = "✗ " + (e as Error).message;
  } finally {
    feedbackBusy.value = false;
  }
}

async function restore(count: number) {
  const ok = confirm(
    `Przywrócić ${count} plik(ów) do stanu sprzed naprawy?\n\n` +
      "UWAGA: to cofa również przeniesienie plików ZAINFEKOWANYCH — strona wróci do stanu sprzed skanu.",
  );
  if (!ok) return;
  try {
    const d = await api.post<{ jobId: string }>(`/api/runs/${encodeURIComponent(props.runId)}/restore`, {});
    restoreJob.value = d.jobId;
  } catch (e) {
    alert((e as Error).message);
  }
}

const vClass = (v: string) => "v-" + v.replace(/[^a-z]/gi, "");
const actionText: Record<string, string> = {
  quarantined: "kwarantanna", repaired: "naprawiony", none: "—", skipped: "pominięty", failed: "nieoceniony",
};
</script>

<template>
  <div class="page">
    <a class="back" :href="href('naprawy')"><Icon name="arrow-left" /> Wszystkie naprawy</a>

    <p v-if="err" class="err">{{ err }}</p>
    <p v-else-if="!run" class="empty">ładowanie…</p>

    <template v-else>
      <div class="head">
        <div>
          <h1><Icon name="globe" :size="22" /> {{ run.domain }}</h1>
          <p class="muted">
            {{ when(run.createdAt) }} • {{ modeText(run.mode) }} • <span class="mono">{{ run.target }}</span>
          </p>
        </div>
        <div class="row">
          <span class="pill big" :class="runPill(run).cls">{{ runPill(run).text }}</span>
          <button v-if="canChat && !showChat" type="button" class="sec small" @click="openChat"><Icon name="chat" /> Asystent AI</button>
          <button v-if="data?.manifest?.actions.length && finished && isOwner" type="button" class="sec small" @click="restore(data!.manifest!.actions.length)">
            <Icon name="undo" /> Cofnij zmiany ({{ data!.manifest!.actions.length }})
          </button>
        </div>
      </div>
      <p v-if="!isOwner" class="adminnote">Oglądasz naprawę klienta jako administrator.</p>

      <div class="card">
        <h2>{{ liveJobId ? "Przebieg na żywo" : "Podsumowanie" }}</h2>
        <div class="body">
          <ScanProgress :progress="run.progress" :running="!!liveJobId" />
          <p v-if="!run.progress && liveJobId" class="muted">Uruchamiam…</p>
          <p v-if="!run.progress && !liveJobId" class="muted">Ta naprawa nie ma zapisanego podsumowania (wykonana starszą wersją panelu).</p>
          <LogStream v-if="liveJobId" :job-id="liveJobId" @progress="onProgress" @done="onDone" />
        </div>
      </div>

      <!-- Ankieta po naprawie + dopracowywanie -->
      <div v-if="showFeedback" class="card feedback" :class="{ answered: run.feedback }">
        <div class="body">
          <!-- Stan: potwierdzone „działa" -->
          <template v-if="run.feedback === 'ok' && !askingHelp">
            <p class="q okc"><Icon name="check" /> Potwierdziłeś {{ when(run.feedbackAt!) }}, że strona działa prawidłowo.</p>
            <button type="button" class="link" @click="reportAgain">Jednak coś nie działa?</button>
          </template>

          <!-- Stan: zgłoszony problem, czekamy na poprawkę / ponowne sprawdzenie -->
          <template v-else-if="run.feedback === 'broken' && !askingHelp">
            <p class="q critc">Zgłosiłeś {{ when(run.feedbackAt!) }}, że strona nie działa prawidłowo.</p>
            <blockquote v-if="lastProblem?.message" class="quote">{{ lastProblem.message }}</blockquote>
            <div v-if="lastReply" class="reply">
              <div class="rh"><Icon name="chat" :size="14" /> Odpowiedź devopsa • {{ when(lastReply.createdAt) }}</div>
              <p>{{ lastReply.message }}</p>
            </div>
            <p class="muted small">
              {{ lastReply ? "Sprawdź stronę jeszcze raz i daj znać:" : lastProblem?.via === "devops" ? "Zgłoszenie jest u devopsa. Gdy dostaniesz odpowiedź, sprawdź stronę i daj znać:" : "Gdy poprawka zostanie wprowadzona, sprawdź stronę i daj znać:" }}
            </p>
            <div class="row">
              <button type="button" :disabled="feedbackBusy" @click="sendFeedback(true)"><Icon name="check" /> Sprawdziłem, działa</button>
              <button type="button" class="sec" :disabled="feedbackBusy" @click="reportAgain"><Icon name="x" /> Nadal nie działa / coś innego</button>
              <button v-if="canChat && !showChat" type="button" class="sec" @click="openChat"><Icon name="chat" /> Wróć do asystenta</button>
            </div>
          </template>

          <!-- Stan: pytanie lub opis problemu -->
          <template v-else>
            <p class="q">Czy strona <b>{{ run.domain }}</b> działa teraz tak, jak powinna?</p>
            <p v-if="!askingHelp" class="muted small">
              Sprawdź najważniejsze podstrony, formularze i panel administracyjny. Automat zweryfikował
              tylko, czy strona zwraca poprawny HTML — zepsutych styli czy formularzy sam nie zauważy.
            </p>
            <div v-if="!askingHelp" class="row">
              <button type="button" :disabled="feedbackBusy" @click="sendFeedback(true)"><Icon name="check" /> Tak, działa</button>
              <button type="button" class="sec" :disabled="feedbackBusy" @click="askingHelp = true"><Icon name="x" /> Nie, coś nie działa</button>
            </div>

            <!-- Krok 1: opis. Krok 2: asystent AI (w cenie) albo devops (płatny). -->
            <div v-else class="help">
              <div class="field">
                <label for="fb-msg">Co nie działa? Napisz, co widzisz i gdzie (adres podstrony, komunikat błędu).</label>
                <textarea id="fb-msg" v-model="feedbackMsg" rows="3" maxlength="4000" placeholder="np. strona główna ładuje się bez styli; formularz kontaktowy na /kontakt zwraca błąd 500; po zalogowaniu do panelu biała strona…" />
              </div>
              <div v-if="!wantDevops" class="opts">
                <button type="button" class="pick" :disabled="!msgOk || feedbackBusy" @click="sendFeedback(false, 'assist')">
                  <span class="ic"><Icon name="chat" :size="24" /></span>
                  <span class="grow">
                    <span class="t">Popraw z asystentem AI <span class="pill ok">w cenie naprawy</span></span>
                    <span class="d">Twój opis trafi do asystenta od razu. Ma dostęp do plików i logów strony, znajdzie przyczynę i zaproponuje poprawkę; każdą zmianę zatwierdzasz.</span>
                  </span>
                </button>
                <button type="button" class="pick" :disabled="!msgOk || feedbackBusy" @click="wantDevops = true">
                  <span class="ic"><Icon name="briefcase" :size="24" /></span>
                  <span class="grow">
                    <span class="t">Poproś o pomoc devopsa <span class="pill warn">{{ hourly }} zł / h</span></span>
                    <span class="d">Człowiek zajmie się stroną ręcznie. Rozliczenie po wykonaniu, za faktyczny czas pracy.</span>
                  </span>
                </button>
              </div>
              <template v-else>
                <p class="price">
                  <Icon name="briefcase" /> Pomoc devopsa to usługa dodatkowo płatna: <b>{{ hourly }} zł za godzinę</b> pracy.
                  Rozliczenie po wykonaniu, na podstawie faktycznie przepracowanego czasu. Klikając
                  „Poproś o pomoc", akceptujesz tę stawkę.
                </p>
                <div class="row">
                  <button type="button" :disabled="feedbackBusy || !msgOk" @click="sendFeedback(false, 'devops')">
                    <Icon name="life-buoy" /> {{ feedbackBusy ? "Wysyłam…" : "Poproś o pomoc devopsa" }}
                  </button>
                  <button type="button" class="sec" :disabled="feedbackBusy" @click="wantDevops = false"><Icon name="arrow-left" /> Wróć</button>
                </div>
              </template>
              <button v-if="!wantDevops" type="button" class="link" :disabled="feedbackBusy" @click="askingHelp = false">Anuluj</button>
            </div>
          </template>
          <p v-if="feedbackInfo" class="hint" style="margin-top:8px">{{ feedbackInfo }}</p>
          <p v-if="support" class="hint">Numer zgłoszenia: <code>{{ support.id.slice(0, 8) }}</code></p>

          <!-- Historia zgłoszeń: widać, jak naprawa była dopracowywana -->
          <details v-if="history.length > 1 || (history.length === 1 && history[0].kind !== 'ok')" class="hist">
            <summary>Historia zgłoszeń ({{ history.length }})</summary>
            <ol>
              <li v-for="e in history" :key="e.id" :class="e.kind">
                <span class="muted small">{{ when(e.createdAt) }}</span>
                <b>{{ kindText[e.kind] }}</b>
                <span v-if="e.kind === 'problem' && viaText[e.via]" class="muted small">• {{ viaText[e.via] }}</span>
                <span v-if="e.kind === 'reply' && e.author" class="muted small">• {{ e.author }}</span>
                <div v-if="e.message" class="hm">{{ e.message }}</div>
              </li>
            </ol>
          </details>
        </div>
      </div>

      <!-- Czat z asystentem AI -->
      <div v-if="showChat && canChat" class="card chatcard">
        <h2><Icon name="chat" /> Asystent AI <span class="spacer" /><button type="button" class="link" @click="showChat = false">zwiń</button></h2>
        <div class="body">
          <AssistChat :run-id="runId" :is-admin="!isOwner" :domain="run.domain" :seed="chatSeed" @seeded="chatSeed = null" @escalate="escalate" />
        </div>
      </div>

      <div v-if="restoreJob" class="card">
        <h2>Cofanie zmian</h2>
        <div class="body"><LogStream :job-id="restoreJob" @done="emit('restored')" /></div>
      </div>

      <!-- Szczegóły techniczne -->
      <div v-if="data?.report" class="card">
        <h2>Szczegóły techniczne</h2>
        <div class="body">
          <details :open="problems.length > 0 && problems.length <= 30 ? true : undefined">
            <summary>Wykryte pliki ({{ problems.length }} z {{ data.report.findings?.length ?? 0 }} ocenionych)</summary>
            <div class="scroll">
              <table v-if="problems.length">
                <thead><tr><th>Plik</th><th>Werdykt</th><th>Pewność</th><th>Co zrobiono</th></tr></thead>
                <tbody>
                  <tr v-for="f in problems" :key="f.path">
                    <td style="word-break:break-all">{{ f.path }}<div v-if="f.note" class="note">{{ f.note }}</div></td>
                    <td><span :class="vClass(f.verdict)">{{ f.verdict }}</span></td>
                    <td>{{ Math.round(f.confidence * 100) }}%</td>
                    <td>{{ actionText[f.actionTaken] ?? f.actionTaken }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="muted" style="padding:10px 0">Nie wykryto złośliwych plików.</p>
            </div>
          </details>

          <details v-if="data.report.systemFindings?.length">
            <summary>Skan systemu ({{ data.report.systemFindings.length }})</summary>
            <ul>
              <li v-for="(s, i) in data.report.systemFindings" :key="i" class="muted">
                <b>{{ s.category }}</b>: {{ s.title }} — <code>{{ s.detail }}</code>
              </li>
            </ul>
          </details>

          <details v-if="data.reportMd">
            <summary>Pełny raport (tekst)</summary>
            <pre class="md">{{ data.reportMd }}</pre>
          </details>

          <details v-if="data.scanLog && !liveJobId">
            <summary>Log skanu (zapis krok po kroku)</summary>
            <pre class="md">{{ data.scanLog }}</pre>
          </details>
          <p class="hint mono" style="margin-top:8px">identyfikator: {{ runId }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.back { display: inline-block; color: var(--mut); text-decoration: none; font-size: 13px; margin-bottom: var(--s3); }
.back:hover { color: var(--fg); }
.head { display: flex; justify-content: space-between; align-items: flex-start; gap: var(--s4); flex-wrap: wrap; margin-bottom: var(--s4); }
.head h1 { font-size: 22px; font-weight: 750; letter-spacing: -.02em; word-break: break-all; }
.head p { margin: 4px 0 0; font-size: 12px; word-break: break-all; }
.pill.big { font-size: 12px; padding: 5px 12px; }
.small { font-size: 12px; }
details > summary { cursor: pointer; color: var(--mut); font-size: 13px; font-weight: 600; margin-top: var(--s2); user-select: none; }
details > summary:hover { color: var(--fg); }
details:first-child > summary { margin-top: 0; }
.scroll { overflow-x: auto; margin-top: var(--s2); }
ul { margin: var(--s2) 0; padding-left: var(--s5); font-size: 12px; }
li { margin-bottom: var(--s1); }
.note { font-size: 11px; color: var(--mut2); }
.md {
  background: var(--logbg); color: #c9d6e6; border: 1px solid var(--bd); border-radius: var(--r2);
  padding: var(--s3); max-height: 400px; overflow: auto; white-space: pre-wrap;
  font: 12px/1.6 var(--mono); margin: var(--s2) 0 0;
}
.v-backdoor { color: var(--crit); font-weight: 700; }
.v-injected { color: var(--warn); font-weight: 600; }
.v-suspicious { color: var(--warn); }
.feedback { border-color: var(--acc); }
.feedback.answered { border-color: var(--bd); }
.q { margin: 0 0 var(--s2); font-weight: 600; font-size: 14px; }
.help { margin-top: var(--s2); }
.price { font-size: 12px; background: var(--warn-dim); color: var(--fg); border-radius: var(--r1); padding: var(--s2) var(--s3); margin: var(--s2) 0; }
.okc { color: var(--ok); } .critc { color: var(--crit); }
.quote { margin: 0 0 var(--s2); padding: var(--s2) var(--s3); border-left: 3px solid var(--crit); background: var(--panel2); font-size: 13px; white-space: pre-wrap; color: var(--fg); }
.reply { margin: 0 0 var(--s2); padding: var(--s2) var(--s3); border: 1px solid var(--acc); border-radius: var(--r1); background: var(--acc-dim); font-size: 13px; }
.reply .rh { font-size: 11px; color: var(--mut); text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px; display: flex; align-items: center; gap: 4px; }
.reply p { margin: 0; white-space: pre-wrap; }
.hist { margin-top: var(--s3); }
.hist ol { list-style: none; padding: 0; margin: var(--s2) 0 0; font-size: 13px; }
.hist li { padding: var(--s2) 0; border-top: 1px solid var(--bd); display: flex; flex-wrap: wrap; gap: 6px; align-items: baseline; }
.hist li b { font-weight: 650; }
.hist li.ok b { color: var(--ok); } .hist li.problem b { color: var(--crit); } .hist li.reply b { color: var(--acc); }
.hist .hm { flex-basis: 100%; white-space: pre-wrap; color: var(--fg); }
.pick:disabled { opacity: .55; cursor: not-allowed; }
.adminnote { font-size: 12px; color: var(--warn); background: var(--warn-dim); border-radius: var(--r1); padding: var(--s2) var(--s3); margin: 0 0 var(--s3); }
.chatcard { border-color: var(--acc); }
.opts { display: flex; flex-direction: column; gap: var(--s2); margin-bottom: var(--s2); }
.pick {
  display: flex; align-items: center; gap: var(--s3); text-align: left; width: 100%;
  background: var(--panel); color: var(--fg); border: 1px solid var(--bd); border-radius: var(--r2);
  padding: var(--s3) var(--s4); font-weight: 400; white-space: normal;
}
.pick:hover:not(:disabled) { border-color: var(--acc); filter: none; }
.pick.on { border-color: var(--acc); background: var(--acc-dim); }
.pick .ic { color: var(--acc); display: grid; place-items: center; }
.pick .t { display: block; font-weight: 650; font-size: 14px; }
.pick .d { display: block; font-size: 12px; color: var(--mut); margin-top: 2px; line-height: 1.5; }
</style>
